import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import invariant from 'tiny-invariant'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { useBoardsContext } from '../context/BoardsContext'
import useBoardMutations from '../hooks/useBoardMutations'
import CardDetail from './CardDetail'

function timeAgo(dateString) {
  if (!dateString) return null
  const seconds = Math.floor((Date.now() - new Date(dateString)) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

function Card({ card, columnId, index }) {
  const cardRef = useRef(null)
  const { canEdit, setSearchQuery, getLabelColor, selectedBoardId } = useBoardsContext()
  const { markCardRead } = useBoardMutations(selectedBoardId)
  const [isDragging, setIsDragging] = useState(false)
  const [closestEdge, setClosestEdge] = useState(null)
  const [showDetail, setShowDetail] = useState(false)

  useEffect(() => {
    const cardElm = cardRef.current
    invariant(cardElm)

    const cleanups = []

    if (canEdit) {
      cleanups.push(
        draggable({
          element: cardElm,
          getInitialData: () => ({ type: 'card', cardId: card.id, columnId }),
          onDragStart: () => setIsDragging(true),
          onDrop: () => setIsDragging(false)
        })
      )
    }

    cleanups.push(
      dropTargetForElements({
        element: cardElm,
        getData: ({ input, element }) => {
          const data = { type: 'card', cardId: card.id, columnId }
          return attachClosestEdge(data, { input, element, allowedEdges: ['top', 'bottom'] })
        },
        getIsSticky: () => true,
        onDragEnter: (args) => {
          if (args.source.data.cardId !== card.id) {
            setClosestEdge(extractClosestEdge(args.self.data))
          }
        },
        onDrag: (args) => {
          if (args.source.data.cardId !== card.id) {
            setClosestEdge(extractClosestEdge(args.self.data))
          }
        },
        onDragLeave: () => setClosestEdge(null),
        onDrop: () => setClosestEdge(null)
      })
    )

    const cleanup = combine(...cleanups)
    return () => { if (typeof cleanup === 'function') cleanup() }
  }, [card.id, columnId, canEdit])

  const statusColor = card.job_status_name ? getLabelColor('statuses', card.job_status_name) : null
  const movedAgo = card.column_changed_at ? timeAgo(card.column_changed_at) : null

  const toggleSearchTerm = (term) => (e) => {
    e.stopPropagation()
    setSearchQuery(prev => {
      if (prev.includes(term)) {
        return prev.replace(term, '').replace(/\s{2,}/g, ' ').trim()
      }
      return prev ? `${prev} ${term}` : term
    })
  }

  return (
    <>
      <div
        ref={cardRef}
        data-tour="board-card"
        className={classNames('kb-card', {
          'kb-card--dragging': isDragging,
          'kb-card--unseen': card.unseen,
          'kb-card--with-stripe': !!card.job_status_name,
        })}
        onClick={() => setShowDetail(true)}
      >
        {card.job_status_name ? (
          <div
            className="kb-card-stripe"
            style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
          >
            <span
              className="kb-card-stripe-status"
              onClick={toggleSearchTerm(card.job_status_name)}
            >
              {card.job_status_name}
            </span>
            {movedAgo && <span className="kb-card-stripe-time">{movedAgo}</span>}
          </div>
        ) : movedAgo && (
          <div className="kb-card-stripe kb-card-stripe--empty">
            <span />
            <span className="kb-card-stripe-time">{movedAgo}</span>
          </div>
        )}

        <div className="kb-card-body">
          <div className="kb-card-row">
            <div className="kb-card-title">{card.job_name}</div>
            <div className="flex items-center gap-1">
              {card.unseen && (
                <button
                  className="text-yellow-500 hover:text-yellow-700 text-xs leading-none focus:outline-none"
                  onClick={(e) => { e.stopPropagation(); markCardRead.mutate({ cardId: card.id, columnId }) }}
                  title="Mark as read"
                >
                  &#10003;
                </button>
              )}
              <button
                className="kb-card-menu"
                onClick={(e) => { e.stopPropagation(); setShowDetail(true) }}
                title="Card options"
              >
                <span /><span /><span />
              </button>
            </div>
          </div>

          {card.job_custom_id && (
            <div className="kb-card-jobid">{card.job_custom_id}</div>
          )}

          {card.extras && card.extras.length > 0 && (
            <dl className="kb-card-extras">
              {card.extras.map((extra, i) => (
                <div key={i} className="kb-card-extras-row">
                  <dt>{extra.label}</dt>
                  <dd>{extra.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {card.tag_list && card.tag_list.length > 0 && (
            <div className="kb-card-tags">
              {card.tag_list.map(tag => {
                const tc = getLabelColor('tags', tag)
                return (
                  <span
                    key={tag}
                    style={{ cursor: 'pointer', backgroundColor: tc.bg, color: tc.text }}
                    onClick={toggleSearchTerm(tag)}
                  >{tag}</span>
                )
              })}
            </div>
          )}
        </div>

        {closestEdge && (
          <div className={`kb-drop-indicator kb-drop-indicator--${closestEdge}`} />
        )}
      </div>

      {showDetail && (
        <CardDetail card={card} columnId={columnId} onClose={() => setShowDetail(false)} />
      )}
    </>
  )
}

Card.propTypes = {
  card: PropTypes.object.isRequired,
  columnId: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired
}

export default React.memo(Card)
